/**
 * MentionInput — textarea replacement that shows a @email typeahead dropdown.
 *
 * Accepted @mention forms: @foo@bar.com or @<foo@bar.com>.
 * When the user types @, candidates are fetched from /api/chat/mentions/candidates.
 * Selecting a candidate inserts @<email> into the text.
 * Free-text (non-candidate) mentions like @someone@example.com are also valid.
 */
import {
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  useCallback,
} from 'react'
import { api } from '../../api/client'
import type { MentionCandidate } from '../../api/client'

// ---------------------------------------------------------------------------
// renderWithMentions — highlight @mentions in message bubbles
// ---------------------------------------------------------------------------

const mentionRe = /@<?([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})>?/g

/** Splits content into text + @mention spans. Safe — no innerHTML, pure React. */
export function renderWithMentions(content: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  mentionRe.lastIndex = 0
  while ((match = mentionRe.exec(content)) !== null) {
    if (match.index > last) {
      parts.push(content.slice(last, match.index))
    }
    parts.push(
      <span
        key={match.index}
        className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700"
      >
        @{match[1]}
      </span>,
    )
    last = match.index + match[0].length
  }
  if (last < content.length) {
    parts.push(content.slice(last))
  }
  return parts
}

// ---------------------------------------------------------------------------
// MentionInput
// ---------------------------------------------------------------------------

interface MentionInputProps {
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void
  disabled?: boolean
  placeholder?: string
  rows?: number
  textareaRef?: React.RefObject<HTMLTextAreaElement>
  className?: string
  /** Inline style for the textarea — the Chat composer uses it to set a height. */
  style?: React.CSSProperties
}

export function MentionInput({
  value,
  onChange,
  onKeyDown,
  disabled,
  placeholder,
  rows = 2,
  textareaRef: externalRef,
  className,
  style,
}: MentionInputProps) {
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const textareaRef = externalRef ?? internalRef

  const [candidates, setCandidates] = useState<MentionCandidate[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [mentionStart, setMentionStart] = useState<number | null>(null)
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Find the @-prefix being typed at the cursor position.
  function getMentionQuery(text: string, cursor: number): { start: number; query: string } | null {
    const before = text.slice(0, cursor)
    const match = /(?:^|[\s\n])@([^\s@]*)$/.exec(before)
    if (!match) return null
    const start = before.length - 1 - match[1].length // position of @
    return { start, query: match[1] }
  }

  const closePicker = useCallback(() => {
    setDropdownOpen(false)
    setCandidates([])
    setMentionStart(null)
  }, [])

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value
    onChange(text)

    const cursor = e.target.selectionStart ?? text.length
    const mention = getMentionQuery(text, cursor)
    if (!mention) {
      closePicker()
      return
    }

    setMentionStart(mention.start)
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    fetchTimerRef.current = setTimeout(async () => {
      try {
        const result = await api.listMentionCandidates(mention.query)
        const list = result.candidates ?? []
        if (list.length > 0) {
          setCandidates(list)
          setSelectedIdx(0)
          setDropdownOpen(true)
        } else {
          closePicker()
        }
      } catch {
        closePicker()
      }
    }, 120)
  }

  function insertCandidate(candidate: MentionCandidate) {
    if (mentionStart === null) return
    const cursor = textareaRef.current?.selectionStart ?? value.length
    const before = value.slice(0, mentionStart)
    const after = value.slice(cursor)
    const inserted = `@<${candidate.email}> `
    onChange(before + inserted + after)
    closePicker()
    // Restore focus after React re-render
    setTimeout(() => {
      const el = textareaRef.current
      if (el) {
        el.focus()
        const pos = (before + inserted).length
        el.setSelectionRange(pos, pos)
      }
    }, 0)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (dropdownOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx(i => Math.min(i + 1, candidates.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx(i => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        // Tab always inserts; Enter inserts only when dropdown is open.
        if (candidates[selectedIdx]) {
          e.preventDefault()
          insertCandidate(candidates[selectedIdx])
          return
        }
      }
      if (e.key === 'Escape') {
        closePicker()
        return
      }
    }
    onKeyDown?.(e)
  }

  // Close picker on outside click.
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (textareaRef.current && !textareaRef.current.contains(e.target as Node)) {
        closePicker()
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [closePicker, textareaRef])

  // Cleanup debounce on unmount.
  useEffect(() => {
    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    }
  }, [])

  return (
    /* `min-w-0` is not optional. A flex item defaults to `min-width: auto`, which refuses to
       shrink below the content's intrinsic minimum — for a textarea that is its `cols`, about
       20 characters. Without it this box holds ~180px open and the row's deficit is paid by
       whatever sits beside it, which is how the composer ended up as a sliver next to a Send
       button on a phone. */
    <div className="relative flex-1 min-w-0">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        className={className}
        style={style}
      />
      {dropdownOpen && candidates.length > 0 && (
        <ul
          role="listbox"
          className="absolute bottom-full mb-1 left-0 w-64 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
        >
          {candidates.map((c, i) => (
            <li
              key={c.email}
              role="option"
              aria-selected={i === selectedIdx}
              onMouseDown={e => { e.preventDefault(); insertCandidate(c) }}
              className={`px-3 py-1.5 cursor-pointer text-sm ${
                i === selectedIdx ? 'bg-indigo-50 text-indigo-700' : 'text-gray-800 hover:bg-gray-50'
              }`}
            >
              {c.display_name ? (
                <span>{c.display_name} <span className="text-gray-400 text-xs">({c.email})</span></span>
              ) : (
                <span>{c.email}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
